import { useCallback } from 'react';

import { useClient } from 'urql';

import { CostChannel } from '@sg/graphql-schema';

import { type OrderChannel, type UpsellItem } from '../../machine/ordering-machine.types';
import {
  BagUpsellItemsDocument,
  type BagUpsellItemsQuery,
  type BagUpsellItemsQueryVariables,
} from './graphql/BagUpsellItems.generated';

/**
 * Fetches the upsell items used by the upsell placement experiment. Runs alongside useFetchInitialData.
 */
export const useFetchUpsellItems = () => {
  const client = useClient();

  const fetchUpsellItems = useCallback(
    async (params: FetchUpsellItemsParams): Promise<UpsellItem[]> => {
      const { restaurantId, orderChannel, productIdsInCart } = params;

      if (!restaurantId) {
        return [];
      }

      const response = await client
        .query<BagUpsellItemsQuery, BagUpsellItemsQueryVariables>(
          BagUpsellItemsDocument,
          {
            restaurantId,
            costChannel:
              orderChannel === 'delivery'
                ? CostChannel.DeliveryCostChannel
                : CostChannel.DefaultCostChannel,
          },
          { requestPolicy: 'cache-and-network' },
        )
        .toPromise();

      return mapUpsellItems(response.data?.categoryUpsells, productIdsInCart);
    },
    [client],
  );

  return { fetchUpsellItems };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mapUpsellItems(
  categoryUpsells: BagUpsellItemsQuery['categoryUpsells'] | undefined,
  productIdsInCart: string[],
): UpsellItem[] {
  if (!categoryUpsells) {
    return [];
  }

  const firstCategory = categoryUpsells.find((category) => category.categoryRanking === 1);

  if (!firstCategory) {
    return [];
  }

  const productIdsInCartSet = new Set(productIdsInCart);

  return firstCategory.products
    .filter((product) => !product.outOfStock && !productIdsInCartSet.has(product.id))
    .map((product) => ({
      id: product.id,
      name: product.name,
      cost: product.cost,
      channelCost: product.channelCost,
      imageUrl: product.imageUrl,
      outOfStock: product.outOfStock,
      menuId: product.menuId,
    }));
}

// ─── types ───────────────────────────────────────────────────────────────────

export type FetchUpsellItemsParams = {
  restaurantId: string;
  orderChannel: OrderChannel;
  productIdsInCart: string[];
};
